import React, { useState, useEffect } from 'react';
import { Modal, Button, Checkbox, Space, Typography, Alert, Statistic, Row, Col, message } from 'antd';
import axios from 'axios';
import { API_URL } from '../config';

const { Text, Paragraph } = Typography;

interface SyncRoomNamesModalProps {
  visible: boolean;
  hotelId: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface PreviewStats {
  totalRooms: number;
  eligibleRooms: number;
  roomsWithExistingExternalOrEmName: number;
  roomsWithoutRoomType: number;
  roomsWithRoomTypeMissingName: number;
}

const SyncRoomNamesModal: React.FC<SyncRoomNamesModalProps> = ({
  visible,
  hotelId,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [overwrite, setOverwrite] = useState(true);
  const [stats, setStats] = useState<PreviewStats | null>(null);

  useEffect(() => {
    if (visible && hotelId !== undefined) {
      loadPreview();
    } else if (!visible) {
      setStats(null);
      setOverwrite(true);
    }
  }, [visible, hotelId]);

  const loadPreview = async () => {
    try {
      const response = await axios.get(`${API_URL}/rooms/sync-names-preview`, {
        params: { hotelId },
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load preview:', error);
      message.error('Failed to load preview');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/rooms/bulk-sync-names-from-type`, {
        hotelId,
        overwrite,
      });

      const { updated, skipped, noRoomType, noRoomTypeName } = response.data;

      const parts = [`Updated ${updated}`];
      if (skipped > 0) parts.push(`skipped ${skipped} (already had names)`);
      if (noRoomType > 0) parts.push(`${noRoomType} without matching room type`);
      if (noRoomTypeName > 0) parts.push(`${noRoomTypeName} with empty room-type name`);

      message.success(parts.join(', '));
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Sync error:', error);
      message.error(error.response?.data?.message || 'Failed to sync names');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Sync Room Names from Room Type"
      open={visible}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          disabled={!stats || stats.eligibleRooms === 0}
        >
          Sync Names
        </Button>,
      ]}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Copies each room's associated room-type name into the room's{' '}
          <Text code>externalNames</Text> and <Text code>emNames</Text> fields across all languages.
          The room's underlying <Text code>name</Text> (the PMS room number) is not changed.
          Rooms without a matching room type, or whose room type has no name, are skipped.
        </Paragraph>

        {stats && (
          <>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic title="Total Rooms" value={stats.totalRooms} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Eligible"
                  value={stats.eligibleRooms}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Already Have Names"
                  value={stats.roomsWithExistingExternalOrEmName}
                />
              </Col>
            </Row>

            {(stats.roomsWithoutRoomType > 0 || stats.roomsWithRoomTypeMissingName > 0) && (
              <Alert
                type="info"
                showIcon
                message="Some rooms will be skipped"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {stats.roomsWithoutRoomType > 0 && (
                      <li>{stats.roomsWithoutRoomType} room(s) have no matching room type</li>
                    )}
                    {stats.roomsWithRoomTypeMissingName > 0 && (
                      <li>
                        {stats.roomsWithRoomTypeMissingName} room(s) whose room type has no name
                      </li>
                    )}
                  </ul>
                }
              />
            )}

            <Checkbox checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)}>
              Overwrite existing room names
            </Checkbox>

            <Alert
              type={overwrite ? 'warning' : 'success'}
              showIcon
              message={
                overwrite
                  ? `${stats.eligibleRooms} room(s) will be updated. Existing external/EM names will be overwritten.`
                  : `Up to ${stats.eligibleRooms} room(s) will be updated. Rooms with existing names will be kept.`
              }
            />
          </>
        )}
      </Space>
    </Modal>
  );
};

export default SyncRoomNamesModal;
